import type { TranslationKey } from '@/i18n/translation-key';
import { EVENT_SUBSCRIPTION_PRODUCT_KEYS } from '@/constants/event-subscription-product-keys';

export type EventPlanWidgetSurface = 'flow' | 'event';

export type EventPlanCapacityUsageMode = 'full' | 'crew_upload' | 'playout' | 'none';

export type EventPlanScopeIdSource = 'event' | 'flow' | 'eventSlotOrFlow';

export type EventPlanLineDef = {
  role: 'base' | 'secondary';
  productKey: string;
  titleKey: TranslationKey;
  explainerKey: TranslationKey;
  scopeType: 'Event' | 'Flow' | 'EventSlot';
  scopeIdFrom: EventPlanScopeIdSource;
  capacityUsage: EventPlanCapacityUsageMode;
};

export type EventPlanWidgetContextDef = {
  surface: EventPlanWidgetSurface;
  titleKey: TranslationKey;
  hintKey: TranslationKey;
  /** Main product lines shown in this surface (add-ons resolve via parentProductKey against these). */
  lines: EventPlanLineDef[];
};

/**
 * Which plan lines appear where.
 * Future: add `channel` / `eventSpace` surfaces with their own line lists.
 */
export function resolveEventPlanWidgetContext(params: {
  surface: EventPlanWidgetSurface;
  multiFlow: boolean;
}): EventPlanWidgetContextDef | null {
  const { surface, multiFlow } = params;

  if (surface === 'event') {
    // Event-scoped capacity only exists as a product line in multi-flow mode.
    if (!multiFlow) return null;
    return {
      surface: 'event',
      titleKey: 'event.plan.eventWidgetTitle',
      hintKey: 'event.plan.eventWidgetHint',
      lines: [
        {
          role: 'base',
          productKey: EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT,
          titleKey: 'event.plan.basePlanSection',
          explainerKey: 'event.plan.eventBasePlanExplainer',
          scopeType: 'Event',
          scopeIdFrom: 'event',
          capacityUsage: 'crew_upload',
        },
      ],
    };
  }

  // Flow surface
  if (multiFlow) {
    return {
      surface: 'flow',
      titleKey: 'event.plan.flowWidgetTitle',
      hintKey: 'event.plan.flowWidgetHint',
      lines: [
        {
          role: 'base',
          productKey: EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW,
          titleKey: 'event.plan.basePlanSection',
          explainerKey: 'event.plan.flowPlayoutExplainer',
          scopeType: 'Flow',
          scopeIdFrom: 'flow',
          capacityUsage: 'playout',
        },
        {
          role: 'secondary',
          productKey: EVENT_SUBSCRIPTION_PRODUCT_KEYS.EVENT_BROADCAST,
          titleKey: 'event.plan.broadcastSection',
          explainerKey: 'event.plan.broadcastExplainer',
          scopeType: 'EventSlot', // resolved to EventSlot or Flow via scopeIdFrom
          scopeIdFrom: 'eventSlotOrFlow',
          capacityUsage: 'none',
        },
      ],
    };
  }

  return {
    surface: 'flow',
    titleKey: 'event.plan.flowWidgetTitle',
    hintKey: 'event.plan.flowWidgetHint',
    lines: [
      {
        role: 'base',
        productKey: EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY,
        titleKey: 'event.plan.basePlanSection',
        explainerKey: 'event.plan.basePlanExplainer',
        scopeType: 'Flow',
        scopeIdFrom: 'flow',
        capacityUsage: 'full',
      },
      {
        role: 'secondary',
        productKey: EVENT_SUBSCRIPTION_PRODUCT_KEYS.EVENT_BROADCAST,
        titleKey: 'event.plan.broadcastSection',
        explainerKey: 'event.plan.broadcastExplainer',
        scopeType: 'EventSlot',
        scopeIdFrom: 'eventSlotOrFlow',
        capacityUsage: 'none',
      },
    ],
  };
}

/** Capacity parent keys that Stage Ready (and similar) may attach to. */
export const EVENT_CAPACITY_PARENT_PRODUCT_KEYS = [
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY,
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT,
] as const;

/**
 * Whether a catalog add-on's `parentProductKey` belongs to a main line shown in this widget.
 * Capacity parents are interchangeable so Stage Ready works for single (`base_capacity`)
 * and multi (`base_capacity_event`) without duplicating catalog rows.
 */
export function addonParentMatchesContextLine(addonParentKey: string, contextProductKey: string): boolean {
  const parent = addonParentKey.trim();
  const line = contextProductKey.trim();
  if (!parent || !line) return false;
  if (parent === line) return true;
  const capacityParents = EVENT_CAPACITY_PARENT_PRODUCT_KEYS as readonly string[];
  return capacityParents.includes(parent) && capacityParents.includes(line);
}
